import React, { useState } from "react";
import { Eye, Download, RefreshCw } from "lucide-react";
import useGetHealthLogs from "../../hooks/useGetHealthLogs";

import Graph from "../graph";
import GetQueryCsvApi from "../../api/getQueryCsvApi";
import LoadingSpinner from "../components/LoadingSpinner";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Button } from "components/ui/button";

function formatQueryName(str) {
    const firstLetter = str.charAt(0).toUpperCase();
    const restOfString = str.slice(1);
    const formattedString = restOfString.replace(/_/g, " ");
    return firstLetter + formattedString;
}

function ErrorAlert({ error }) {
    const errorMessage = typeof error === 'string' ? error :
                        error?.message ||
                        error?.response?.data?.message ||
                        "An error occurred";
    return (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <strong>Query Error:</strong> {errorMessage}
        </div>
    );
}

function TableComponent({ alertList }) {
    let cardAlertList = alertList;
    let isMoreAlerts = false;
    if (alertList.length > 8) {
        cardAlertList = alertList.slice(0, 8);
        isMoreAlerts = true;
    }
    return (
        <>
            <div className="overflow-x-auto">
                <table className="w-full text-center text-sm">
                    <thead>
                        <tr className="border-b border-border text-muted-foreground">
                            {Object.keys(alertList[0]).map((key) => (
                                <th scope="col" key={key} className="px-2 py-1.5 font-medium">
                                    {key}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="text-xs">
                        {cardAlertList.map((row, index) => (
                            <tr key={index} className="border-b border-border/60">
                                {Object.values(row).map((col, colIndex) => (
                                    <td key={colIndex} className="px-2 py-1.5">{col}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {isMoreAlerts ? (
                <p className="mt-2 text-sm text-destructive">More alerts on details window</p>
            ) : (
                ""
            )}
        </>
    );
}

function OkComponent() {
    return <p className="text-sm text-success">Everything looks ok!</p>;
}

function HealthCard({
    queryName,
    eventId,
    environment,
    startTime,
    endTime,
    chartHeight,
    openDetails,
}) {
    const [queryId, setQueryId] = useState(null);
    const { queryLogs, error, isLoaded, isTimeOut, refetchQueryResults } = useGetHealthLogs(
        queryName,
        eventId,
        environment,
        startTime,
        endTime,
        setQueryId
    );

    const handleDetailsViewClick = () => {
        openDetails("health", formatQueryName(queryName), queryLogs, {
            queryId,
            queryName,
            eventId,
            environment,
        });
    };

    const downloadCsvFile = () => {
        GetQueryCsvApi(queryId, queryName, eventId, environment, startTime, endTime);
    };

    const TimeOutAlert = () => {
        return (
            <div className="flex items-center justify-center gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning">
                <span><strong>Time Out!</strong> Please refresh after some time</span>
                <Button size="icon-sm" onClick={refetchQueryResults} title="Refresh">
                    <RefreshCw />
                </Button>
            </div>
        );
    };

    let cardBodyContent;
    if (error) {
        cardBodyContent = <ErrorAlert error={error} />;
    } else if (isLoaded) {
        if (!queryLogs || Object.keys(queryLogs).length === 0) {
            cardBodyContent = <ErrorAlert error="No data returned from query" />;
        } else if (queryLogs["alert_list"] && queryLogs["alert_list"].length > 0) {
            cardBodyContent = <TableComponent alertList={queryLogs["alert_list"]} />;
        } else if (queryLogs["health_card"] && queryLogs["health_card"].includes("graph") && queryLogs["log_data"] && queryLogs["log_data"].length > 0) {
            cardBodyContent = (
                <Graph
                    log_data={queryLogs["log_data"]}
                    key_list={queryLogs["key_list"]}
                    legend_enabled={false}
                    height={chartHeight}
                />
            );
        } else {
            cardBodyContent = <OkComponent />;
        }
    } else if (isTimeOut) {
        cardBodyContent = <TimeOutAlert />;
    } else {
        cardBodyContent = <LoadingSpinner />;
    }

    return (
        <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0 border-b border-border py-3">
                <CardTitle className="text-sm">{formatQueryName(queryName)}</CardTitle>
                <div className="flex gap-1.5">
                    <Button
                        size="icon-sm"
                        onClick={downloadCsvFile}
                        title="Download CSV"
                        disabled={!isLoaded}
                    >
                        <Download />
                    </Button>
                    <Button
                        size="icon-sm"
                        onClick={handleDetailsViewClick}
                        title="View details"
                        disabled={!isLoaded}
                    >
                        <Eye />
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="flex min-h-[120px] items-center justify-center pt-4 text-center">
                <div className="w-full">{cardBodyContent}</div>
            </CardContent>
        </Card>
    );
}

export default HealthCard;
